import get from 'lodash/get';
import find from 'lodash/find';
import { FormControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Component, ComponentFactoryResolver, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { UiChooseMotiveModalComponent } from './ui-choose-motive-modal/ui-choose-motive-modal.component';
import { ApiService, dataHelper, ngHelper, currencyParser, CieUtilsModalManagerService, CustomComponent } from '@cakeiteasy/web-utils';

@Component({
  standalone: false,
  selector: 'ui-cake-build-motive',
  templateUrl: './ui-cake-build-motive.component.html',
  styleUrls: ['./ui-cake-build-motive.component.scss']
})
export class UiCakeBuildMotiveComponent extends CustomComponent implements OnChanges {
  @Input() printingPictures: any[];
  @Input() activePrinting: any;
  @Input() disabledChooseMotiveModal: boolean;
  @Input() currency = 'kr';
  @Input() maxImageSize?: number;
  @Input() imagesForChooseSrc: string[];

  @Output() onSelect = new EventEmitter<any>();
  @Output() onRemove = new EventEmitter<void>();
  @Output() onImageUploading = new EventEmitter<Promise<any>>();

  public isCropping = false;
  public imageUploading: Promise<any>;
  public options: any[];

  public optionControl: FormControl;

  constructor(private _api: ApiService,
              private _translate: TranslateService,
              private _modalService: CieUtilsModalManagerService,
              private _componentResolver: ComponentFactoryResolver) {
    super();
    this.init();
  }

  public ngOnChanges(changes: SimpleChanges) {
    ngHelper.ifChanged(changes, 'printingPictures', () => {
      this.init();
    });

    ngHelper.ifChanged(changes, 'activePrinting', () => {
      this.init();
    });
  }

  init() {
    if (!this.printingPictures || !this.printingPictures.length) {
      return;
    }

    this.options = this._prepareOptions(this.printingPictures, this.currency);

    this.optionControl = new FormControl(this.activePrinting && this.activePrinting.id);

    this.addSubscriber(
      this.optionControl.valueChanges
        .subscribe(value => {
          this.selectOption(value);
        })
    );

    if (this.activePrinting) {
      if (!this._getMotiveProduct(this.activePrinting.id)) {
        this.selectNo();
      } else {
        this.selectOption(this.activePrinting.id);
      }
    }
  }

  selectNo() {
    const noOption = find(this.options, { id: 0 });
    this.activePrinting = noOption;
    this.onSelect.emit(noOption);
  }

  _prepareOptions(printings, currency) {

    const printingOptions = printings.map((printing) => {
      return {
        name: `${this._translate.instant('common.yes')} +${currencyParser(printing.price, currency)}`,
        id: printing.id
      };
    });

    const newOptions = [
      {
        name: this._translate.instant('common.no'),
        id: 0
      }].concat(printingOptions);

    return dataHelper.leftJoin(this.options, newOptions, 'id');
  }

  _getMotiveProduct(id) {
    return this.printingPictures.find(function (printing) {
      return printing.id === id;
    });
  }

  public selectOption(id?: number) {
    if (id) {
      const url = get(this.activePrinting, 'url');
      this.activePrinting = find(this.printingPictures, { id });
      this.activePrinting.url = url;

      if (!url) {
        if (this.disabledChooseMotiveModal) {
          this.isCropping = true;
        } else {
          this.showMotiveModal();
        }
      } else {
        this.onSelect.emit(this.activePrinting);
      }
    } else {
      this.selectNo();
    }
  }

  onImageCropped(imgSrc: string) {
    // $segment.trackAttempToAddMotive($scope.orderLine);
    this.isCropping = false;
    return this.save(imgSrc)
      .then((url?: string) => {
        this.activePrinting.url = url;
        this.onSelect.emit(this.activePrinting);
        return url;
      })
      .catch(() => {
        if (!this.activePrinting || !this.activePrinting.url) {
          this.cancel();
          this.selectNo();
        }
      });
  }

  showMotiveModal(url?: string) {
    // $segment.trackAttempToAddMotive($scope.orderLine);
    return this._modalService
      .show(UiChooseMotiveModalComponent, this._componentResolver,
        { imgUrl: url, maxImageSize: this.maxImageSize, imagesForChooseSrc: this.imagesForChooseSrc },
        { backdrop: 'static' })
      .then((imgSrc) => {
        this.activePrinting.url = imgSrc;
        return this.save(imgSrc);
      })
      .then((url?: string) => {
        this.activePrinting.url = url;
        this.onSelect.emit(this.activePrinting);
        return url;
      })
      .catch(() => {
        if (!this.activePrinting || !this.activePrinting.url) {
          this.cancel();
          this.selectNo();
        }
      });
  }

  cancel() {
    this.onRemove.emit();
  }

  save(imgSrc: string): Promise<string> {
    if (!imgSrc) {
      this.selectNo();
      // $scope.orderLine.motiveFormat = eMotiveFormats.none;
      return;
    }

    // if base64
    const base64Str = ';base64,';
    if (imgSrc.indexOf(base64Str) === -1) {
      this.activePrinting.url = imgSrc;
      this.onSelect.emit(this.activePrinting);

      if (imgSrc.indexOf('http') === 0) {
        return Promise.resolve(imgSrc);
      }

      return Promise.resolve('');
    }

    this.imageUploading = firstValueFrom(this._api
      .post('api.extra', 'images', null, {
        image: imgSrc,
        fileName: Date.now() + '-' + Math.random() + '.jpg'
      }))
      .then((uploaded) => {
        return uploaded.image.originalUrl;
      })
      .catch(() => {
        return null;
      });

    this.onImageUploading.emit(this.imageUploading);

    return this.imageUploading;
  }
}

